<template>
    <page :title="$t('main_campsiteReservation')" :breadcrumb="breadcrumb">
        <div class="progress-steps">
            <div class="row">
                <div class="col-sm-3 col-md-3">
                  <router-link v-if="!booking" class="progress-step done" :to="{name: 'Detail', params: {lang: language, id: $route.params.campsite}}" exact>
                    <i class="fa fa-check"></i>
                    <span class="title">{{ $t('reservation_selectCampsite') }}</span>
                  </router-link>
                    <div v-else class="progress-step done">
                        <i class="fa fa-check"></i>
                        <span class="title">{{ $t('reservation_selectCampsite') }}</span>
                    </div>
                </div>
                <div class="col-sm-3 col-md-3">
                  <p class="progress-step active">
                    <i class="fa fa-bookmark"></i>
                    <span class="title">{{ $t('reservation_reservation') }}</span>
                  </p>
                </div>
                <div class="col-sm-3 col-md-3">
                  <p class="progress-step inactive">
                    <i class="fa fa-list"></i>
                    <span class="title">{{ $t('reservation_summary') }}</span>
                  </p>
                </div>
                <div class="col-sm-3 col-md-3">
                  <p class="progress-step inactive">
                    <i class="fa fa-shopping-cart"></i>
                    <span class="title">{{ $t('reservation_checkout') }}</span>
                  </p>
                </div>
            </div>
        </div>
        <form @submit.prevent="reservation" v-if="campsite">
            <div class="listing-hero mb-3">
                <div class="listing-hero-inner">
                    <div class="container">
                        <div v-if="!campsite.mainimage" class="listing-hero-image hidden-sm-down"></div>
                        <div v-if="campsite.mainimage" class="listing-hero-image hidden-sm-down" :title="campsite.mainimage.name"
                             :style="`background-image: url(${campsite.mainimage.source})`"></div>
                        <h1>{{ campsite.campsitename }} <i class="fa fa-check hidden-sm-down"></i></h1>
                        <address>
                            {{ campsite.number }} {{ campsite.street }}<br>
                            {{ campsite.city }}, {{ campsite.country }} {{ campsite.zipcode }}
                        </address>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-sm-8">
                    <div @change="calculate">
                        <div class="row">
                            <div :class="{'col-md-12': !campsite.switchtimeallowed, 'col-md-6': campsite.switchtimeallowed}">
                                <div class="form-group">
                                    <label for="startdate">{{ $t('reservation_startDate') }}</label>
                                    <datepicker id="startdate"
                                                v-model="formData.startdate"
                                                :monday-first="true"
                                                :placeholder="$t('reservation_selectStartDate')"
                                                :language="dateLang"
                                                @input="dateChange"
                                                :highlighted="highlighted"
                                                :disabledDates="disabledDates"
                                                :bootstrap-styling="true"></datepicker>
                                </div>
                            </div>
                            <div class="col-md-6" v-if="campsite.switchtimeallowed">
                                <div class="form-group">
                                    <label for="persons">{{ $t('reservation_arrival') }}</label>
                                    <vue-timepicker v-model="formData.switchtimearrival" format="hh:mm" :minute-interval="15"></vue-timepicker>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div :class="{'col-md-12': !campsite.switchtimeallowed, 'col-md-6': campsite.switchtimeallowed}">
                                <div class="form-group">
                                    <label for="enddate">{{ $t('reservation_endDate') }}</label>
                                    <datepicker id="enddate"
                                                v-model="formData.enddate"
                                                :monday-first="true"
                                                :placeholder="$t('reservation_selectEndDate')"
                                                :language="dateLang"
                                                @input="dateChange"
                                                :highlighted="highlighted"
                                                :disabledDates="disabledEndDates"
                                                :bootstrap-styling="true"></datepicker>
                                </div>
                            </div>
                            <div class="col-md-6" v-if="campsite.switchtimeallowed">
                                <div class="form-group">
                                    <label for="persons">{{ $t('reservation_departure') }}</label>
                                    <vue-timepicker v-model="formData.switchtimedeparture" format="hh:mm" :minute-interval="15"></vue-timepicker>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col">
                                <div class="form-group" :class="{'has-danger': errors.has('persons')}">
                                    <label for="persons">{{ $t('reservation_numberOfPersons') }}</label>
                                    <input v-model="formData.persons"
                                           v-validate="`required|min_value:1|max_value:${this.campsite.maxpersons === 0 ? 10000 : this.campsite.maxpersons}`"
                                           :data-vv-as="$t('reservation_numberOfPersons')"
                                           min="1"
                                           max="100"
                                           name="persons"
                                           :class="{'form-control-danger': errors.has('persons')}"
                                           id="persons" type="number" class="form-control">
                                    <small class="form-text text-danger" v-if="errors.has('persons')">
                                        {{ errors.first('persons') }}
                                    </small>
                                </div>
                            </div>
                            <div class="col" v-if="campsite.dogsallowed" :class="{'has-danger': errors.has('dogs')}">
                                <div class="form-group">
                                    <label for="dogs">{{ $t('reservation_numberOfDogs') }}</label>
                                    <input v-model="formData.dogs"
                                           :data-vv-as="$t('reservation_numberOfDogs')"
                                           min="0"
                                           max="100"
                                           name="dogs"
                                           :class="{'form-control-danger': errors.has('dogs')}"
                                           id="dogs" type="number" class="form-control">
                                    <small class="form-text text-danger" v-if="errors.has('dogs')">
                                        {{ errors.first('dogs') }}
                                    </small>
                                </div>
                            </div>
                        </div>
                        <div>
                          <img v-if="campsite.mapimage" :src="campsite.mapimage.source" :alt="campsite.mapimage.name"/>
                        </div>
                        <div class="form-group" :class="{'has-danger': errors.has('pitch')}">
                            <label for="pitch">{{ $t('reservation_pitch') }}</label>
                            <select class="form-control limitedselect" id="pitch"
                                    :class="{'form-control-danger': errors.has('pitch')}"
                                    v-validate="'required'"
                                    :data-vv-as="$t('reservation_pitch')"
                                    name="pitch"
                                    v-model="formData.pitchid" :readonly="sortedPitched.length === 0">
                                <option :value="null" disabled v-if="sortedPitched.length > 0">{{ $t('reservation_selectPitch') }}</option>
                                <option :value="null" disabled v-if="sortedPitched.length === 0">{{ $t('reservation_noPitchAvailable') }}</option>
                                <option v-for="(place, index) in sortedPitched" :key="index"
                                        :value="place.id">{{ $t('reservation_pitch') }} {{place.pitchnumber}} {{place.description}}</option>
                            </select>
                            <small class="form-text text-danger" v-if="errors.has('pitch')">
                                {{ errors.first('pitch') }}
                            </small>
                        </div>
                        <div class="form-group">
                            <label for="current">{{ $t('reservation_current') }}</label>
                            <select class="form-control" id="current" v-model="formData.current">
                                <option value="0">0A</option>
                                <option value="10">10A</option>
                                <option value="16">16A</option>
                                <option value="32">32A</option>
                            </select>
                        </div>
                    </div>

                    <h6 class="page-title-small mt-5">{{ $t('account_personalInformation') }}</h6>
                    <div class="row">
                        <div class="col-md-4">
                            <div class="form-group" :class="{'has-danger': errors.has('firstname')}">
                                <label for="firstname">{{ $t('account_firstName') }}</label>
                                <input v-model="user.firstname"
                                       v-validate="'required'"
                                       :data-vv-as="$t('account_firstName')"
                                       name="firstname"
                                       :class="{'form-control-danger': errors.has('firstname')}"
                                       id="firstname" type="text" class="form-control">
                                <small class="form-text text-danger" v-if="errors.has('firstname')">
                                    {{ errors.first('firstname') }}
                                </small>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="form-group">
                                <label for="middlename">{{ $t('account_middleName') }}</label>
                                <input v-model="user.middlename"
                                       :data-vv-as="$t('account_middleName')"
                                       id="middlename" type="text" class="form-control">
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="form-group" :class="{'has-danger': errors.has('lastname')}">
                                <label for="lastname">{{ $t('account_lastName') }}</label>
                                <input v-model="user.lastname"
                                       v-validate="'required'"
                                       :data-vv-as="$t('account_lastName')"
                                       name="lastname"
                                       :class="{'form-control-danger': errors.has('lastname')}"
                                       id="lastname" type="text" class="form-control">
                                <small class="form-text text-danger" v-if="errors.has('lastname')">
                                    {{ errors.first('lastname') }}
                                </small>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group" :class="{'has-danger': errors.has('licenseplate')}">
                                <label for="licenseplate">{{ $t('account_licensePlate') }}</label>
                                <input v-model="user.licenseplate"
                                       v-validate="'required'"
                                       :data-vv-as="$t('account_licensePlate')"
                                       name="licenseplate"
                                       :class="{'form-control-danger': errors.has('licenseplate')}"
                                       id="licenseplate" type="text" class="form-control">
                                <small class="form-text text-danger" v-if="errors.has('licenseplate')">
                                    {{ errors.first('licenseplate') }}
                                </small>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group" :class="{'has-danger': errors.has('email')}">
                                <label for="email">{{ $t('account_email') }}</label>
                                <input :readonly="user.id" v-model="user.email"
                                       v-validate="'required|email'"
                                       :data-vv-as="$t('account_email')"
                                       name="email"
                                       :class="{'form-control-danger': errors.has('email')}"
                                       id="email" type="email" class="form-control">
                                <small class="form-text text-danger" v-if="errors.has('email')">
                                    {{ errors.first('email') }}
                                </small>
                            </div>
                        </div>
                    </div>
                    <button v-if="!user.id && !booking" class="btn btn-secondary btn-block" @click.prevent.stop="toggleSidebar">
                        <i aria-hidden="true" class="fa fa-sign-in-alt"></i> {{ $t('reservation_autoFill') }}
                    </button>
                </div>
                <div class="col-md-4">
                    <div class="pricing mt-4">
                        <div class="pricing-col-wrapper">
                            <div class="pricing-col">
                                <h2 class="pricing-title">{{ $t('reservation_pricing') }}</h2>
                                <ul class="pricing-features">
                                    <li>{{ $t('reservation_price_totalamount') }} <span class="pricing-partial">{{ pricing.totalamount || '' }}</span></li>
                                    <li>{{ $t('reservation_price_taxtotalamount') }} <span class="pricing-partial">{{ pricing.taxtotalamount || '' }}</span></li>
                                </ul>
                                <div class="pricing-price">
                                    <span class="pricing-value">{{pricing.totalamountincludingtax || '?'}}</span>
                                </div>
                            </div>
                            <div class="mt-1">
                                <button class="btn btn-secondary btn-block"
                                        @click.prevent="calculate"
                                        :disabled="$wait.is('calculate')"
                                >
                                    <span v-if="!$wait.is('calculate')">
                                        <i class="fa fa-calculator" aria-hidden="true"></i> {{ $t('reservation_calculate') }}
                                    </span>
                                    <span v-if="$wait.is('calculate')">
                                        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> {{ $t('reservation_calculating') }}
                                    </span>

                                </button>
                                <button type="submit" class="btn btn-primary btn-block"
                                        :disabled="$wait.is('checkout') || errors.any()"
                                >
                                    <span v-if="!$wait.is('checkout')">
                                        <i class="fa fa-shopping-cart" aria-hidden="true"></i> {{ $t('reservation_checkout') }}
                                    </span>
                                    <span v-if="$wait.is('checkout')">
                                        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> {{ $t('reservation_working') }}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </page>
</template>

<script>
import Page from './utilities/Page'
import { API } from '../api/axios'
import { mapGetters, mapMutations } from 'vuex'
import Datepicker from 'vuejs-datepicker'
import VueTimepicker from 'vue2-timepicker'
import { de, en, es, fr, nl } from 'vuejs-datepicker/dist/locale'
import * as qs from 'qs'
import moment from 'moment'

export default {
  name: 'Reservation',
  components: {Page, Datepicker, VueTimepicker},
  props: {
    mode: {
      type: String,
      default: 'campsite'
    },
  },
  data () {
    return {
      formData: {
        startdate: new Date(),
        enddate: moment(new Date()).add(1, 'days').toDate(),
        persons: 1,
        dogs: 0,
        current: 0,
        switchtimearrival: null,
        switchtimedeparture: null,
        pitchid: null,
      },
      importedLanguages: {
        // @TODO: Hard-coded languages!
        en, es, fr, de, nl,
      },
      highlighted: {
        to: () => this.formData.enddate,
        from: () => this.formData.startdate,
        includeDisabled: true,
      },
      disabledDates: {
        to: moment(new Date()).subtract(1, 'days').toDate(),
      },
      loaded: false,
      campsite: null,
      placelist: null,
      pricing: {
        stayamount: null,
        personamount: null,
        currentamount: null,
        dogamount: null,
        totalamountincludingtax: null,
      },
    }
  },
  computed: {
    ...mapGetters(['language', 'user', 'savedQueryParams', 'jail']),
    booking () {
      return this.mode === 'ecpWidget'
    },
    queryParams () {
      let params = {
        ...this.formData,
        languageId: this.language,
        campsiteid: this.$route.params.campsite,
      }

      params.switchtimearrival === null && delete params['switchtimearrival']
      params.switchtimedeparture === null && delete params['switchtimedeparture']

      params.startdate = this.startDate
      params.enddate = this.endDate

      params.firstname = this.user.firstname
      params.middlename = this.user.middlename
      params.lastname = this.user.lastname
      params.email = this.user.email
      params.licenseplate = this.user.licenseplate
      params.onetimecustomer = !this.user.id

      return qs.stringify(params)
    },
    startDate () {
      return moment(this.formData.startdate).format('YYYY-MM-DD')
    },
    endDate () {
      return moment(this.formData.enddate).format('YYYY-MM-DD')
    },
    disabledEndDates () {
      return {
        to: moment(this.formData.startdate).add(1, 'days').toDate(),
      }
    },
    breadcrumb () {
      if (!this.campsite) {
        return null
      }

      let campsiteId = this.$route.params.campsite
      return [
        {
          name: 'Listings',
          query: this.savedQueryParams
        },
        {
          name: 'Detail',
          params: {lang: this.language, id: campsiteId},
          display: this.campsite.campsitename,
        },
      ]
    },
    dateLang () {
      return this.importedLanguages[this.language]
    },
    sortedPitched () {
      let pitched = this.campsite.pitchlist.slice()
      return pitched.sort((a, b) => a.pitchnumber - b.pitchnumber)
    },
  },
  methods: {
    ...mapMutations(['toggleSidebar', 'setJail']),
    getCampsite () {
      let campsiteId = this.$route.params.campsite

      API.get(`/campsites/${campsiteId}/${this.language}/${this.startDate}/${this.endDate}`)
        .then(response => {
          this.campsite = response.data
        })
    },
    dateChange () {
      let startdate = moment(this.formData.startdate)
      let enddate = moment(this.formData.enddate)

      if (startdate.isSameOrAfter(enddate, 'day')) {
        this.formData.enddate = moment(startdate).add(1, 'days').toDate()
      }

      // Update highlights
      this.highlighted.from = this.formData.startdate
      this.highlighted.to = this.formData.enddate
      this.highlighted.dates = [this.formData.startdate]

      this.getCampsite()
      this.calculate()
    },
    calculate () {
      this.$wait.start('calculate')
      API.get(`/bookings/calculation?${this.queryParams}`)
        .then(response => {
          if (response.data === null) {
            return Promise.reject(new Error('Response was null!'))
          } else {
            this.pricing = response.data
            this.pricing.maxdogs = +this.pricing.maxdogs

            if (this.pricing.maxdogs !== 0 && this.pricing.maxdogs < this.formData.dogs) {
              this.errors.add({field: 'dogs', msg: `Maximum allowed dogs (${this.pricing.maxdogs}) exceeded.`})
            }
          }
        })
        .catch(error => {
          if (error.response.data) {
            if (error.response.data === 'Pitch not found') {
              this.errors.add({
                field: 'pitch',
                msg: 'Select a pitch!'
              })
            }
          }
          console.error('Calculation error', error)
        })
        .then(() => {
          this.$wait.end('calculate')
        })
    },
    reservation () {
      this.$validator.validate().then(result => {
        if (result) {
          this.$wait.start('checkout')
          API.post(`/bookings/confirm?${this.queryParams}`)
            .then(response => {
              if (response.data === null) {
                return Promise.reject(new Error('Response was null!'))
              } else {
                this.$store.commit('addReservation', response.data)
                this.$router.push({name: this.booking ? 'BookingCheckout' : 'Checkout', params: {lang: this.language}})
              }
            })
            .catch(error => {
              console.error('Checkout error', error)
            })
            .then(() => {
              this.$wait.end('checkout')
            })
        }
      })
    },
  },
  mounted () {
    if (this.booking) this.setJail(true)
    this.getCampsite()
  },
}
</script>

<style lang="scss">
    /*@import '../scss/helpers/variables';*/

</style>



// WEBPACK FOOTER //
// src/components/Reservation.vue